import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Bot, X, Sparkles, Send, Copy, Check, RefreshCw,
    FileText, MessageSquare, List, Mic, Lightbulb,
    ChevronRight, FilePlus, Wand2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import planningAiService from '../../../ai/services/planningAiService';
import { smartCopy } from '../../../utils/textFormatting';

// Quick action suggestions for podcast planning
const QUICK_ACTIONS = [
    {
        id: 'briefing',
        icon: FileText,
        label: 'Guest briefing',
        prompt: 'Create a guest briefing for {guestName}. Include their background, key achievements, and interesting angles to explore.'
    },
    {
        id: 'questions',
        icon: MessageSquare,
        label: 'Interview questions',
        prompt: 'Generate 8-10 interview questions for {guestName}. Mix of foundational and deep-dive questions.'
    },
    {
        id: 'outline',
        icon: List,
        label: 'Episode outline',
        prompt: 'Create an episode outline for an interview with {guestName}. Include intro, main segments, and outro.'
    },
    {
        id: 'intro',
        icon: Mic,
        label: 'Intro script',
        prompt: 'Write an engaging intro script for {guestName}. Make it energetic and highlight why listeners should be excited.'
    },
    {
        id: 'talking-points',
        icon: Lightbulb,
        label: 'Talking points',
        prompt: 'Identify 5-7 key talking points for an interview with {guestName}. Focus on unique insights they can share.'
    }
];

// Animation variants
const messageVariants = {
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: 'spring', stiffness: 500, damping: 30 }
    },
    exit: {
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.15 }
    }
};

const containerVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { type: 'spring', stiffness: 400, damping: 25 }
    },
    exit: {
        opacity: 0,
        y: 20,
        scale: 0.95,
        transition: { duration: 0.2 }
    }
};

const AiAssistant = ({ context, onInsertContent, onClearContext }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [followUpQuestions, setFollowUpQuestions] = useState([]);
    const [copied, setCopied] = useState(false);
    const [localContext, setLocalContext] = useState(null);

    const chatContainerRef = useRef(null);
    const textareaRef = useRef(null);

    // Sync local context with prop context
    useEffect(() => {
        setLocalContext(context);
    }, [context]);

    // Listen for openMariko event from paragraph controls
    useEffect(() => {
        const handleOpenMariko = () => {
            setIsOpen(true);
        };

        window.addEventListener('openMariko', handleOpenMariko);
        return () => window.removeEventListener('openMariko', handleOpenMariko);
    }, []);

    // Handle clearing context
    const handleClearContext = useCallback(() => {
        setLocalContext(null);
        if (onClearContext) {
            onClearContext();
        }
    }, [onClearContext]);

    // Auto-resize textarea based on content
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            // Reset height to auto to get the correct scrollHeight
            textarea.style.height = 'auto';
            // Set height based on scrollHeight, with a max height
            const newHeight = Math.min(textarea.scrollHeight, 120);
            textarea.style.height = `${newHeight}px`;
        }
    };

    // Adjust textarea height when input value changes
    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTo({
                top: chatContainerRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [chatHistory]);

    // Focus input when opened
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            setTimeout(() => textareaRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Reset state when closed
    useEffect(() => {
        if (!isOpen) {
            setChatHistory([]);
            setFollowUpQuestions([]);
            setInputValue('');
        }
    }, [isOpen]);

    const handleSendMessage = async (message = inputValue) => {
        if (!message.trim() || isGenerating) return;

        const userMessage = message.trim();
        setInputValue('');

        // Add user message to history
        setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsGenerating(true);
        setFollowUpQuestions([]);

        try {
            const result = await planningAiService.chat({
                message: userMessage,
                context: localContext,
                chatHistory: chatHistory
            });

            // Add AI response to history
            setChatHistory(prev => [...prev, { role: 'assistant', content: result.response }]);
            setFollowUpQuestions(result.followUpQuestions || []);
        } catch (error) {
            console.error('Chat error:', error);
            setChatHistory(prev => [...prev, {
                role: 'assistant',
                content: 'Sorry, something went wrong. Could you try again?'
            }]);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleQuickActionClick = (action) => {
        // Populate input with the prompt instead of sending
        const prompt = action.prompt.replace('{guestName}', localContext?.guestName || 'the guest');
        setInputValue(prompt);
        textareaRef.current?.focus();
    };

    const handleFollowUpClick = (question) => {
        handleSendMessage(question);
    };

    const handleInsertContent = (content) => {
        if (onInsertContent && content) {
            onInsertContent(content);
        }
    };

    const handleCopyContent = async (content) => {
        try {
            await smartCopy(content);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleRegenerate = () => {
        if (chatHistory.length >= 2) {
            const lastUserMessage = [...chatHistory].reverse().find(m => m.role === 'user');
            if (lastUserMessage) {
                setChatHistory(prev => prev.slice(0, -1));
                handleSendMessage(lastUserMessage.content);
            }
        }
    };

    const formatMessage = (content) => {
        return content.split('\n').map((line, index) => {
            // Headers
            if (line.startsWith('### ')) {
                return <h4 key={index} className="font-semibold text-slate-800 mt-4 mb-1.5 text-sm">{line.slice(4)}</h4>;
            }
            if (line.startsWith('## ')) {
                return <h3 key={index} className="font-bold text-slate-900 mt-5 mb-2">{line.slice(3)}</h3>;
            }
            if (line.startsWith('# ')) {
                return <h2 key={index} className="font-bold text-lg text-slate-900 mt-5 mb-2">{line.slice(2)}</h2>;
            }
            // Bold
            if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={index} className="font-semibold text-slate-800 my-1">{line.slice(2, -2)}</p>;
            }
            // List items
            if (line.startsWith('- ') || line.startsWith('* ')) {
                return (
                    <li key={index} className="ml-4 text-slate-700 my-0.5 flex items-start gap-2">
                        <span className="text-slate-400 mt-1.5">•</span>
                        <span>{line.slice(2)}</span>
                    </li>
                );
            }
            if (line.match(/^\d+\.\s/)) {
                return (
                    <li key={index} className="ml-4 text-slate-700 my-0.5 flex items-start gap-2">
                        <span className="text-indigo-500 font-medium min-w-[1.25rem]">{line.match(/^\d+/)[0]}.</span>
                        <span>{line.replace(/^\d+\.\s/, '')}</span>
                    </li>
                );
            }
            // Empty lines
            if (line.trim() === '') {
                return <div key={index} className="h-2" />;
            }
            // Regular text
            return <p key={index} className="text-slate-700 my-0.5 leading-relaxed">{line}</p>;
        });
    };

    const lastAssistantMessage = [...chatHistory].reverse().find(m => m.role === 'assistant');

    return (
        <div className="absolute top-6 right-6 z-50 flex flex-col items-end pointer-events-none">
            {/* Toggle Button */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    pointer-events-auto
                    w-12 h-12 rounded-2xl shadow-lg flex items-center justify-center
                    transition-all duration-300
                    ${isOpen
                        ? 'bg-slate-800 text-white shadow-slate-300/50'
                        : 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 text-white shadow-indigo-500/30 hover:shadow-xl hover:shadow-indigo-500/40'
                    }
                `}
            >
                <AnimatePresence mode="wait">
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <X size={20} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="sparkles"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                        >
                            <Sparkles size={20} />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Main AI Interface */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="pointer-events-auto mt-3 w-[440px] h-[580px] bg-white rounded-2xl shadow-2xl shadow-slate-200/60 border border-slate-200/80 overflow-hidden flex flex-col"
                    >
                        {/* Header - Fixed at top */}
                        <div className="flex-shrink-0 px-5 py-4 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-sm">
                                    <Wand2 size={16} className="text-white" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-slate-800">AI Assistant</h3>
                                    <p className="text-xs text-slate-500">
                                        {localContext?.selectedText
                                            ? 'Context added - ask about it!'
                                            : localContext?.guestName
                                                ? `Planning for ${localContext.guestName}`
                                                : 'Ready to help'
                                        }
                                    </p>
                                </div>
                            </div>

                            {/* Selected Context Indicator */}
                            {localContext?.selectedText && (
                                <div className="mt-3 p-2 pr-8 bg-indigo-50 rounded-lg border border-indigo-100 relative">
                                    <p className="text-[10px] font-medium text-indigo-600 uppercase tracking-wide mb-1">Selected Context:</p>
                                    <p className="text-xs text-slate-600 line-clamp-2 pr-4">
                                        "{localContext.selectedText}"
                                    </p>
                                    <button
                                        onClick={handleClearContext}
                                        className="absolute top-2 right-2 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition-colors"
                                        title="Remove context"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Chat Area - Scrollable middle section */}
                        <div
                            ref={chatContainerRef}
                            className="flex-1 overflow-y-auto p-5 space-y-4 scroll-smooth min-h-0"
                        >
                            {/* Welcome State */}
                            {chatHistory.length === 0 && !isGenerating && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex flex-col items-center justify-center h-full text-center py-8"
                                >
                                    <motion.div
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ delay: 0.1 }}
                                        className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 via-purple-100 to-pink-100 flex items-center justify-center mx-auto mb-4 shadow-sm"
                                    >
                                        <Sparkles size={22} className="text-indigo-600" />
                                    </motion.div>
                                    <motion.h4
                                        initial={{ y: 10, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.15 }}
                                        className="text-base font-medium text-slate-800 mb-1"
                                    >
                                        How can I help?
                                    </motion.h4>
                                    <motion.p
                                        initial={{ y: 10, opacity: 0 }}
                                        animate={{ y: 0, opacity: 1 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-sm text-slate-500 max-w-[280px]"
                                    >
                                        Ask anything about your podcast planning or use a quick action below
                                    </motion.p>
                                </motion.div>
                            )}

                            {/* Chat Messages */}
                            <AnimatePresence mode="popLayout">
                                {chatHistory.map((message, index) => (
                                    <motion.div
                                        key={index}
                                        variants={messageVariants}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                        layout
                                        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                    >
                                        <div
                                            className={`max-w-[92%] rounded-2xl px-4 py-3 ${message.role === 'user'
                                                ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-br-lg shadow-sm'
                                                : 'bg-slate-100 text-slate-800 rounded-bl-lg'
                                                }`}
                                        >
                                            {message.role === 'assistant' ? (
                                                <div className="text-sm leading-relaxed">
                                                    {formatMessage(message.content)}
                                                </div>
                                            ) : (
                                                <p className="text-sm leading-relaxed">{message.content}</p>
                                            )}
                                        </div>
                                    </motion.div>
                                ))}
                            </AnimatePresence>

                            {/* Loading State */}
                            {isGenerating && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex justify-start"
                                >
                                    <div className="bg-slate-100 rounded-2xl rounded-bl-lg px-4 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <div className="flex gap-1">
                                                <motion.span
                                                    animate={{ y: [0, -4, 0] }}
                                                    transition={{ duration: 0.5, repeat: Infinity, delay: 0 }}
                                                    className="w-1.5 h-1.5 bg-slate-400 rounded-full"
                                                />
                                                <motion.span
                                                    animate={{ y: [0, -4, 0] }}
                                                    transition={{ duration: 0.5, repeat: Infinity, delay: 0.15 }}
                                                    className="w-1.5 h-1.5 bg-slate-400 rounded-full"
                                                />
                                                <motion.span
                                                    animate={{ y: [0, -4, 0] }}
                                                    transition={{ duration: 0.5, repeat: Infinity, delay: 0.3 }}
                                                    className="w-1.5 h-1.5 bg-slate-400 rounded-full"
                                                />
                                            </div>
                                            <span className="text-xs text-slate-500">Thinking...</span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Follow-up Questions */}
                            {!isGenerating && followUpQuestions.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-2 pt-3"
                                >
                                    <p className="text-xs font-medium text-slate-500 px-1">Follow up:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {followUpQuestions.map((question, index) => (
                                            <motion.button
                                                key={index}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => handleFollowUpClick(question)}
                                                className="text-xs px-3.5 py-2 rounded-full bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 hover:from-indigo-100 hover:to-purple-100 transition-all border border-indigo-200/60 shadow-sm"
                                            >
                                                {question}
                                            </motion.button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Action Buttons for Last Response */}
                            {!isGenerating && lastAssistantMessage && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex items-center gap-2 pt-3 border-t border-slate-100 mt-4"
                                >
                                    <button
                                        onClick={() => handleInsertContent(lastAssistantMessage.content)}
                                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                                    >
                                        <FilePlus size={14} />
                                        Insert
                                    </button>
                                    <button
                                        onClick={() => handleCopyContent(lastAssistantMessage.content)}
                                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                    >
                                        {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                                        {copied ? 'Copied' : 'Copy'}
                                    </button>
                                    <button
                                        onClick={handleRegenerate}
                                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                    >
                                        <RefreshCw size={14} />
                                        Retry
                                    </button>
                                </motion.div>
                            )}
                        </div>

                        {/* Input Area with Quick Actions - Fixed at bottom */}
                        <div className="flex-shrink-0 p-4 border-t border-slate-100 bg-gradient-to-b from-white to-slate-50/50 space-y-3">
                            {/* Selection-based Quick Actions */}
                            {localContext?.selectedText && (
                                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleSendMessage(`Improve this text: "${localContext.selectedText}"`)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
                                    >
                                        <Wand2 size={12} />
                                        Improve
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleSendMessage(`Expand on this: "${localContext.selectedText}"`)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700 text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
                                    >
                                        <ChevronRight size={12} />
                                        Expand
                                    </motion.button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleSendMessage(`Suggest follow-up questions based on: "${localContext.selectedText}"`)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-700 text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
                                    >
                                        <MessageSquare size={12} />
                                        Questions
                                    </motion.button>
                                </div>
                            )}

                            {/* Quick Actions - Horizontal Scrollable Pills */}
                            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                                {QUICK_ACTIONS.map((action) => (
                                    <motion.button
                                        key={action.id}
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={() => handleQuickActionClick(action)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 hover:text-slate-900 text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0"
                                    >
                                        <action.icon size={12} />
                                        {action.label}
                                    </motion.button>
                                ))}
                            </div>

                            {/* Input Form */}
                            <form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    handleSendMessage();
                                }}
                                className="relative"
                            >
                                <textarea
                                    ref={textareaRef}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => {
                                        // Submit on Enter (without Shift)
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            handleSendMessage();
                                        }
                                    }}
                                    placeholder="Ask me anything..."
                                    disabled={isGenerating}
                                    rows={1}
                                    className="w-full bg-white border border-slate-200 rounded-xl py-3 pl-4 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all placeholder:text-slate-400 text-slate-700 disabled:bg-slate-50 shadow-sm resize-none overflow-y-auto min-h-[48px] max-h-[120px]"
                                    style={{
                                        height: 'auto',
                                        wordWrap: 'break-word',
                                        whiteSpace: 'pre-wrap'
                                    }}
                                />
                                <motion.button
                                    type="submit"
                                    disabled={!inputValue.trim() || isGenerating}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="absolute right-2 bottom-3 p-2.5 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 disabled:opacity-50 disabled:from-slate-300 disabled:to-slate-300 transition-all shadow-sm"
                                >
                                    <Send size={14} />
                                </motion.button>
                            </form>
                            <p className="text-[11px] text-slate-400 text-center">
                                AI has full context about your guest
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AiAssistant;
